// DNAChat · Painel de Fila do WhatsApp (V4.56)
// - Lista db.whatsapp_queue com filtro por status (pending/sending/sent/failed)
// - Retry manual em qualquer item
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ListChecks, RefreshCw, RotateCw, Loader2, ChevronLeft, Clock, CheckCircle2, XCircle, Send } from 'lucide-react';

const STATUS_TABS = [
  { key: '',        label: 'Todos',    color: 'slate' },
  { key: 'pending', label: 'Pendente', color: 'amber' },
  { key: 'sending', label: 'Enviando', color: 'blue' },
  { key: 'sent',    label: 'Enviado',  color: 'emerald' },
  { key: 'failed',  label: 'Falhou',   color: 'rose' },
];

const fmtDT = (iso) => { try { return new Date(iso).toLocaleString('pt-BR'); } catch { return iso || '—'; } };

export default function WhatsAppQueuePanel() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = status ? `?status=${status}&limit=300` : '?limit=300';
      const { data } = await api.get(`/dnachat/whatsapp/queue${qs}`);
      setItems(data?.items || []);
      setCounts(data?.counts || {});
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao carregar fila');
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const retry = async (item) => {
    setRetrying(item.id);
    try {
      const { data } = await api.post(`/dnachat/whatsapp/queue/${item.id}/retry`);
      if (data?.success) toast.success(`Reenviado · ID ${String(data.provider_message_id || '').slice(0, 12)}`);
      else toast.error(data?.error || 'Falha ao reenviar');
      fetchData();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : JSON.stringify(d).slice(0, 200));
    } finally { setRetrying(null); }
  };

  const statusPill = (s) => {
    const map = {
      pending: ['bg-amber-50 border-amber-200 text-amber-700', Clock],
      sending: ['bg-blue-50 border-blue-200 text-blue-700', Loader2],
      sent:    ['bg-emerald-50 border-emerald-200 text-emerald-700', CheckCircle2],
      failed:  ['bg-rose-50 border-rose-200 text-rose-700', XCircle],
    };
    const [cls, Icon] = map[s] || ['bg-slate-100 border-slate-200 text-slate-600', ListChecks];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase font-bold ${cls}`}>
        <Icon className={`w-3 h-3 ${s === 'sending' ? 'animate-spin' : ''}`} /> {s}
      </span>
    );
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="wa-queue">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <button type="button" onClick={() => navigate('/v2/chat/whatsapp/config')}
            className="text-xs text-slate-500 hover:text-slate-800 mb-1 inline-flex items-center gap-1"
            data-testid="wa-queue-back">
            <ChevronLeft className="w-3.5 h-3.5" /> Voltar para Config
          </button>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <ListChecks className="w-8 h-8 text-slate-700" /> Fila do WhatsApp
          </h1>
          <p className="text-sm text-slate-500 mt-1">Itens enfileirados por broadcasts. Reenvio manual disponível em qualquer status.</p>
        </div>
        <button type="button" onClick={fetchData} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold disabled:opacity-50"
          data-testid="wa-queue-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Atualizar
        </button>
      </div>

      {/* Tabs de status com contadores */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="wa-queue-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key || 'all'}
            type="button"
            onClick={() => setStatus(t.key)}
            className={`px-3 py-1.5 rounded-lg border text-sm font-semibold flex items-center gap-2 ${
              status === t.key
                ? `bg-${t.color}-600 border-${t.color}-700 text-white`
                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
            data-testid={`wa-queue-tab-${t.key || 'all'}`}
          >
            {t.label}
            {t.key && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${status === t.key ? 'bg-white/20 text-white' : `bg-${t.color}-50 text-${t.color}-700`}`}>
                {counts[t.key] ?? 0}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="wa-queue-table">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Criado</th>
                <th className="text-left px-3 py-2">Destinatário</th>
                <th className="text-left px-3 py-2">Prévia</th>
                <th className="text-center px-3 py-2">Tentativas</th>
                <th className="text-center px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Erro</th>
                <th className="text-right px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-sm">
                  {status ? `Nenhum item com status "${status}".` : 'Fila vazia.'}
                </td></tr>
              )}
              {items.map((it) => {
                const payload = it.payload || {};
                const preview = payload.text || payload.caption || (payload.template_name ? `template: ${payload.template_name}` : (payload.media_id ? `mídia (${payload.media_type})` : '—'));
                return (
                  <tr key={it.id} className="border-t border-slate-100" data-testid={`wa-queue-row-${it.id.slice(0, 8)}`}>
                    <td className="px-3 py-2 text-[11px] text-slate-600 whitespace-nowrap">{fmtDT(it.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="text-[12px] font-mono text-slate-800">{it.phone_normalized}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{it.guardian_id?.slice(0, 12)}…</div>
                    </td>
                    <td className="px-3 py-2 text-[12px] text-slate-700 max-w-xs truncate" title={preview}>{preview}</td>
                    <td className="px-3 py-2 text-center text-slate-700 font-mono text-[12px]">{it.attempts ?? 0}</td>
                    <td className="px-3 py-2 text-center">{statusPill(it.status)}</td>
                    <td className="px-3 py-2 text-[10px] text-rose-700 max-w-xs truncate" title={it.error_detail || ''}>{it.error_detail || ''}</td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" onClick={() => retry(it)} disabled={retrying === it.id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-semibold disabled:opacity-50"
                        data-testid={`wa-queue-retry-${it.id.slice(0, 8)}`}>
                        {retrying === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                        Reenviar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
